/**
 * Ticker
 * A fixed-height strip showing its items in a row. When they don't all fit
 * it becomes a ticker tape: the row (repeated once, for a seamless loop)
 * crawls steadily right to left and wraps round. New items only ever join at
 * the right-hand end, so nothing already on screen moves; a completely new
 * set of items (a new betting round, the hand's results) starts from the
 * left. When the items fit again the tape finishes its lap and stops exactly
 * where it started — no snapping back. Its size never depends on its
 * content. Still for anyone who has reduced motion switched on.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Crawl speed, in CSS pixels per second. */
const SPEED = 35;

export interface TickerItem {
  key: string;
  node: React.ReactNode;
}

interface TickerProps {
  items: TickerItem[];
  /** Height class in px (e.g. "h-[24px]": the root font size is smaller on phones) — fixed, whatever the content. */
  heightClass: string;
  /** Shown when there are no items. */
  empty: React.ReactNode;
  testId?: string;
  className?: string;
}

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const Ticker: React.FC<TickerProps> = ({ items, heightClass, empty, testId, className = '' }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLDivElement>(null);
  const [crawling, setCrawling] = useState(false);
  const offset = useRef(0);
  const stopAtWrap = useRef(false);
  const prevKeys = useRef<string[]>([]);

  const apply = () => {
    if (trackRef.current) trackRef.current.style.transform = `translateX(${-offset.current}px)`;
  };

  // A completely new set of items starts from the left.
  useLayoutEffect(() => {
    const keys = items.map((i) => i.key);
    if (!keys.some((k) => prevKeys.current.includes(k))) {
      offset.current = 0;
      apply();
    }
    prevKeys.current = keys;
  }, [items]);

  // Crawl while the items don't all fit (checked whenever the strip or the
  // row changes size). Stopping waits for the lap to finish.
  const hasItems = items.length > 0;
  useEffect(() => {
    const box = boxRef.current;
    const first = firstRef.current;
    if (!box || !first || typeof ResizeObserver === 'undefined') {
      setCrawling(false);
      offset.current = 0;
      return;
    }
    const check = () => {
      const overflows = !reducedMotion() && first.scrollWidth > box.clientWidth;
      if (overflows) {
        stopAtWrap.current = false;
        setCrawling(true);
      } else {
        stopAtWrap.current = true;
        if (offset.current === 0) setCrawling(false);
      }
    };
    const observer = new ResizeObserver(check);
    observer.observe(box);
    observer.observe(first);
    check();
    return () => observer.disconnect();
  }, [hasItems]);

  // The crawl itself.
  useEffect(() => {
    if (!crawling) return;
    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const lap = firstRef.current?.offsetWidth ?? 0;
      offset.current += SPEED * dt;
      if (lap > 0 && offset.current >= lap) {
        offset.current -= lap;
        if (stopAtWrap.current) {
          offset.current = 0;
          apply();
          setCrawling(false);
          return;
        }
      }
      apply();
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [crawling]);

  return (
    <div
      ref={boxRef}
      data-testid={testId}
      className={`ticker relative flex items-center overflow-hidden rounded-md bg-gray-900/70 border border-gray-800 ${heightClass} ${className}`}
    >
      {!hasItems ? (
        empty
      ) : (
        <div ref={trackRef} className="flex w-max whitespace-nowrap will-change-transform">
          <div ref={firstRef} className="flex shrink-0">
            {items.map((item) => (
              <React.Fragment key={item.key}>{item.node}</React.Fragment>
            ))}
          </div>
          {crawling && (
            <div className="flex shrink-0" aria-hidden>
              {items.map((item) => (
                <React.Fragment key={item.key}>{item.node}</React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Ticker;
